import { Helmet } from 'react-helmet-async';

const SITE_URL = 'https://academy-website-alpha.vercel.app';

// LocalBusiness + Organization (used on HomePage)
export const LocalBusinessSchema = () => {
  const data = {
    '@context': 'https://schema.org',
    '@type': ['LocalBusiness', 'EducationalOrganization'],
    '@id': `${SITE_URL}/#organization`,
    name: 'Aspire Academy',
    alternateName: 'Aspire Academy Sri Lanka',
    description: "Sri Lanka's premier IGCSE and A Level tuition center offering Cambridge and Edexcel programs in Colombo 7.",
    url: SITE_URL,
    logo: `${SITE_URL}/ASPR_ACDMY_16YRS.png`,
    image: `${SITE_URL}/ASPR_ACDMY_16YRS.png`,
    telephone: '+94-74-365-2899',
    email: 'info@aspireacademy.lk',
    foundingDate: '2018',
    priceRange: 'Rs. 2,500 - Rs. 5,000',
    currenciesAccepted: 'LKR',
    paymentAccepted: 'Cash, Bank Transfer',
    address: {
      '@type': 'PostalAddress',
      streetAddress: '102/5A, Srimath Anagarika Dharmapala Mawatha',
      addressLocality: 'Colombo',
      addressRegion: 'Western Province',
      postalCode: '00700',
      addressCountry: 'LK',
    },
    geo: {
      '@type': 'GeoCoordinates',
      latitude: 6.9128705,
      longitude: 79.8571263,
    },
    openingHoursSpecification: [
      {
        '@type': 'OpeningHoursSpecification',
        dayOfWeek: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'],
        opens: '09:00',
        closes: '19:30',
      },
      {
        '@type': 'OpeningHoursSpecification',
        dayOfWeek: 'Sunday',
        opens: '09:00',
        closes: '17:00',
      },
    ],
    sameAs: [
      'https://wa.me/94743652899',
    ],
    hasOfferCatalog: {
      '@type': 'OfferCatalog',
      name: 'Tuition Programs',
      itemListElement: [
        {
          '@type': 'OfferCatalog',
          name: 'IGCSE Programs',
          description: 'Cambridge and Edexcel IGCSE tuition for O Level students',
        },
        {
          '@type': 'OfferCatalog',
          name: 'A Level Programs',
          description: 'Edexcel AS and A2 Level tuition for university preparation',
        },
      ],
    },
    aggregateRating: {
      '@type': 'AggregateRating',
      ratingValue: '4.9',
      reviewCount: '120',
      bestRating: '5',
    },
  };

  return (
    <Helmet>
      <script type="application/ld+json">{JSON.stringify(data)}</script>
    </Helmet>
  );
};

// BreadcrumbList
export const BreadcrumbSchema = ({ items }) => {
  const data = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: items.map((item, index) => ({
      '@type': 'ListItem',
      position: index + 1,
      name: item.name,
      item: item.href ? `${SITE_URL}${item.href}` : undefined,
    })),
  };

  return (
    <Helmet>
      <script type="application/ld+json">{JSON.stringify(data)}</script>
    </Helmet>
  );
};

// Course schema (for subject detail pages)
export const CourseSchema = ({ subject }) => {
  if (!subject) return null;

  const data = {
    '@context': 'https://schema.org',
    '@type': 'Course',
    name: `${subject.level} ${subject.name}`,
    description: subject.description || `${subject.level} ${subject.name} tuition program at Aspire Academy, Colombo.`,
    provider: {
      '@type': 'EducationalOrganization',
      name: 'Aspire Academy',
      url: SITE_URL,
      address: {
        '@type': 'PostalAddress',
        addressLocality: 'Colombo',
        addressCountry: 'LK',
      },
    },
    educationalLevel: subject.level === 'IGCSE' ? 'Secondary' : 'Post-Secondary',
    about: subject.name,
    inLanguage: 'en',
    coursePrerequisites: subject.level === 'A2' ? 'AS Level completion' : undefined,
    hasCourseInstance: {
      '@type': 'CourseInstance',
      courseMode: 'Blended',
      duration: subject.duration || '10 months',
      instructor: subject.tutor_name ? {
        '@type': 'Person',
        name: subject.tutor_name,
      } : undefined,
      offers: {
        '@type': 'Offer',
        category: 'Group Tuition',
        price: subject.group_price_local || 2500,
        priceCurrency: 'LKR',
        availability: 'https://schema.org/InStock',
      },
    },
  };

  return (
    <Helmet>
      <script type="application/ld+json">{JSON.stringify(data)}</script>
    </Helmet>
  );
};

// Course list schema (for IGCSE/A-Levels pages)
export const CourseListSchema = ({ courses, level }) => {
  if (!courses?.length) return null;

  const data = {
    '@context': 'https://schema.org',
    '@type': 'ItemList',
    name: `${level} Subjects at Aspire Academy`,
    description: `Browse all ${level} tuition programs offered at Aspire Academy, Colombo.`,
    numberOfItems: courses.length,
    itemListElement: courses.map((course, index) => ({
      '@type': 'ListItem',
      position: index + 1,
      item: {
        '@type': 'Course',
        name: `${level} ${course.name}`,
        description: course.description,
        url: `${SITE_URL}/igcse/${course.slug}`,
        provider: {
          '@type': 'EducationalOrganization',
          name: 'Aspire Academy',
        },
        offers: {
          '@type': 'Offer',
          price: course.group_price_local || 2500,
          priceCurrency: 'LKR',
        },
      },
    })),
  };

  return (
    <Helmet>
      <script type="application/ld+json">{JSON.stringify(data)}</script>
    </Helmet>
  );
};

// FAQ schema (for About page or any page with FAQs)
export const FAQSchema = ({ faqs }) => {
  if (!faqs?.length) return null;

  const data = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: faqs.map(faq => ({
      '@type': 'Question',
      name: faq.question,
      acceptedAnswer: {
        '@type': 'Answer',
        text: faq.answer,
      },
    })),
  };

  return (
    <Helmet>
      <script type="application/ld+json">{JSON.stringify(data)}</script>
    </Helmet>
  );
};

export default LocalBusinessSchema;
